/**
 * WHAT: Pre-attestation baseline report showing the user access inventory before attestation decisions.
 * WHY:  Before attesters begin reviewing access, stakeholders need a snapshot of who has access,
 *       at what privilege level, and who is assigned to attest each user -- this report serves as
 *       the starting-point evidence for the attestation campaign.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { TopologySection } from './common';
import { formatDateTime, formatDateTimeForReport } from '../utils/dateUtils';

/**
 * WHAT: Renders the pre-attestation baseline for a selected campaign with scope and attester filters.
 * WHY:  Allows campaign administrators and auditors to review the full user access inventory
 *       before attestation begins, verify attester assignments, and export the baseline as CSV
 *       for offline review and regulatory documentation.
 */
const PreAttestationBaselineReport = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [filterAttesterType, setFilterAttesterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();

      if (data.success) {
        // Filter campaigns that are in ATTESTATION or COMPLETED status
        const eligibleCampaigns = data.campaigns.filter(
          campaign => campaign.status === 'ATTESTATION' || campaign.status === 'COMPLETED'
        );
        setCampaigns(eligibleCampaigns);
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const loadReport = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign');
      return;
    }

    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`reports/pre-attestation-baseline/?campaign_id=${selectedCampaignId}`)
      );
      const data = await response.json();

      if (data.success) {
        setReportData(data);
        setCurrentPage(1);
      } else {
        setError(data.error || 'Failed to load report');
      }
    } catch (err) {
      setError('Error loading report: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* WHAT: Downloads the CSV from the server export endpoint.
   * WHY:  The backend (export_pre_attestation_baseline_csv) is the single source
   *       of truth for the CSV — column set, ordering, and values come from the
   *       report table directly. The CSV must never be rebuilt client-side from
   *       the on-screen JSON: two generators drift apart and produce two
   *       versions of the truth (this happened — the client copy was missing
   *       "All Applications"). Same pattern as PostAttestationReport. */
  const exportToCSV = () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign first');
      return;
    }

    try {
      window.open(
        API_CONFIG.getDatabasesEndpoint(`reports/pre-attestation-baseline/export/?campaign_id=${selectedCampaignId}`),
        '_blank'
      );
    } catch (err) {
      setError('Error exporting CSV: ' + err.message);
    }
  };

  const exportToPDF = () => {
    if (!reportData || !reportData.user_access_inventory) {
      setError('Please generate the report first before exporting');
      return;
    }

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to export PDF');
      return;
    }

    const timestamp = formatDateTimeForReport(new Date().toISOString());
    const scopeCriteria = reportData.executive_summary.scope_criteria;

    // Build the PDF content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pre-Attestation Report - ${reportData.executive_summary.campaign_name}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 11px;
          }
          h1 { color: #2c3e50; margin-bottom: 20px; font-size: 20px; }
          h2 { color: #34495e; margin-top: 20px; font-size: 16px; border-bottom: 2px solid #3498db; padding-bottom: 5px; }
          h3 { color: #34495e; margin-top: 15px; font-size: 14px; }
          .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 20px; }
          .summary-item { background: #f8f9fa; padding: 10px; border-radius: 5px; }
          .summary-item strong { display: block; color: #2c3e50; margin-bottom: 3px; }
          .scope-box { background: #e8f4f8; padding: 15px; border-radius: 5px; border: 1px solid #3498db; margin-bottom: 20px; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: bold; }
          .badge-success { background: #27ae60; color: white; }
          .badge-warning { background: #f39c12; color: white; }
          .badge-info { background: #3498db; color: white; }
          .badge-primary { background: #2980b9; color: white; }
          .badge-secondary { background: #95a5a6; color: white; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9px; }
          th { background: #2c3e50; color: white; padding: 6px 4px; text-align: left; font-size: 9px; }
          td { border: 1px solid #ddd; padding: 4px; vertical-align: top; }
          tr:nth-child(even) { background: #f9f9f9; }
          .in-scope { color: #27ae60; font-weight: bold; }
          .out-of-scope { color: #e74c3c; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #7f8c8d; font-size: 10px; border-top: 1px solid #ddd; padding-top: 10px; }
          @media print {
            body { margin: 10px; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
          }
        </style>
      </head>
      <body>
        <h1>Pre-Attestation Report</h1>

        <h2>Executive Summary</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <strong>Campaign Name</strong>
            ${reportData.executive_summary.campaign_name}
          </div>
          <div class="summary-item">
            <strong>Campaign ID</strong>
            ${reportData.executive_summary.campaign_id}
          </div>
          <div class="summary-item">
            <strong>Compliance Framework</strong>
            ${reportData.executive_summary.compliance_framework || 'N/A'}
          </div>
          <div class="summary-item">
            <strong>Database Type</strong>
            ${reportData.executive_summary.database_type || 'N/A'}
          </div>
          <div class="summary-item">
            <strong>Start Date</strong>
            ${formatDateTimeForReport(reportData.executive_summary.start_date)}
          </div>
          <div class="summary-item">
            <strong>End Date</strong>
            ${formatDateTimeForReport(reportData.executive_summary.end_date)}
          </div>
          <div class="summary-item">
            <strong>Campaign Status</strong>
            <span class="badge ${reportData.executive_summary.status === 'COMPLETED' ? 'badge-success' : 'badge-warning'}">
              ${reportData.executive_summary.status}
            </span>
          </div>
          <div class="summary-item">
            <strong>Total Records</strong>
            <span class="badge badge-primary">${reportData.statistics.total_records}</span>
          </div>
          <div class="summary-item">
            <strong>In Scope / Out of Scope</strong>
            <span class="badge badge-success">${reportData.statistics.in_scope_count}</span> /
            <span class="badge badge-secondary">${reportData.statistics.out_of_scope_count}</span>
          </div>
        </div>

        ${scopeCriteria ? `
        <h3>Campaign Scope Selection Criteria</h3>
        <div class="scope-box" style="padding: 8px 12px;">
          <strong>Account Status:</strong>
          <span class="badge ${scopeCriteria.active_only ? 'badge-warning' : 'badge-info'}">
            ${scopeCriteria.active_only ? 'Active Only' : 'All'}
          </span>
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <strong>Privilege Levels:</strong>
          ${scopeCriteria.privilege_levels && scopeCriteria.privilege_levels.length > 0
            ? scopeCriteria.privilege_levels.map(level => `<span class="badge badge-primary">${level}</span>`).join(' ')
            : '<span class="badge badge-info">All</span>'
          }
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <strong>Applied:</strong> ${formatDateTimeForReport(scopeCriteria.applied_date)}
          &nbsp;&nbsp;|&nbsp;&nbsp;
          <strong>Description:</strong> <em>${scopeCriteria.description || 'N/A'}</em>
        </div>
        ` : ''}

        <h2>Complete User Access Inventory</h2>
        <table>
          <thead>
            <tr>
              <th>Database</th>
              <th>DB Type</th>
              <th>App ID</th>
              <th>App Name</th>
              <th>All Applications</th>
              <th>DB Username</th>
              <th>Account Type</th>
              <th>Privilege Level</th>
              <th>Employee</th>
              <th>Email</th>
              <th>Job Title</th>
              <th>Department</th>
              <th>Status</th>
              <th>In Scope</th>
              <th>Exclusion Reason</th>
              <th>Attester</th>
              <th>Attester Type</th>
              <th>Override Applied</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.user_access_inventory.map(record => `
              <tr>
                <td>${record.database_name || ''}</td>
                <td>${record.database_type || ''}</td>
                <td>${record.application_id || ''}</td>
                <td>${record.application_name || ''}</td>
                <td>${record.application_names || ''}</td>
                <td>${record.db_username || ''}</td>
                <td>${record.account_type || ''}</td>
                <td>${record.privilege_level || ''}</td>
                <td>${record.user_display_name || ''}</td>
                <td>${record.user_email || ''}</td>
                <td>${record.user_job_title || ''}</td>
                <td>${record.user_department || ''}</td>
                <td>${record.employment_status || ''}</td>
                <td class="${record.user_in_scope === 'YES' ? 'in-scope' : 'out-of-scope'}">${record.user_in_scope || ''}</td>
                <td>${record.exclusion_reason || ''}</td>
                <td>${record.manager_name || ''}<br/><small>${record.manager_email || ''}</small></td>
                <td>${record.attester_type || ''}</td>
                <td>${record.override_applied || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Report Generated: ${timestamp}</p>
          <p>Pre-Attestation Report - Database Access Management & Attestation Campaign</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Wait for content to load then trigger print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  const getFilteredRecords = () => {
    if (!reportData || !reportData.user_access_inventory) return [];

    return reportData.user_access_inventory.filter(record => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        record.database_name?.toLowerCase().includes(searchLower) ||
        record.db_username?.toLowerCase().includes(searchLower) ||
        record.user_display_name?.toLowerCase().includes(searchLower) ||
        record.user_email?.toLowerCase().includes(searchLower) ||
        record.manager_email?.toLowerCase().includes(searchLower) ||
        record.application_name?.toLowerCase().includes(searchLower);

      // Scope filter
      const matchesScope =
        filterScope === 'all' ||
        (filterScope === 'in_scope' && record.user_in_scope?.toUpperCase() === 'YES') ||
        (filterScope === 'out_of_scope' && record.user_in_scope?.toUpperCase() === 'NO');

      // Attester type filter
      const matchesAttesterType =
        filterAttesterType === 'all' ||
        record.attester_type === filterAttesterType;

      return matchesSearch && matchesScope && matchesAttesterType;
    });
  };

  const getPaginatedRecords = () => {
    const filtered = getFilteredRecords();
    const startIndex = (currentPage - 1) * recordsPerPage;
    return filtered.slice(startIndex, startIndex + recordsPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredRecords().length / recordsPerPage);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterScope, filterAttesterType]);

  // Use formatDateTime from dateUtils for consistent formatting with timezone

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2 className="text-primary mb-4">
            <i className="fas fa-file-alt me-2"></i>
            Pre-Attestation Report
          </h2>

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-filter me-2"></i>
                Report Selection
              </h5>
            </div>
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Select Campaign</label>
                  {isLoadingCampaigns ? (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedCampaignId}
                      onChange={(e) => setSelectedCampaignId(e.target.value)}
                    >
                      <option value="">-- Select a Campaign --</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.campaign_id} value={campaign.campaign_id}>
                          {campaign.campaign_name} ({campaign.status})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={loadReport}
                    disabled={isLoading || !selectedCampaignId}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Loading
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-1"></i>
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-success w-100"
                    onClick={exportToCSV}
                    disabled={!selectedCampaignId || !reportData}
                  >
                    <i className="fas fa-file-csv me-1"></i>
                    CSV
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-danger w-100"
                    onClick={exportToPDF}
                    disabled={!selectedCampaignId || !reportData}
                  >
                    <i className="fas fa-file-pdf me-1"></i>
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {reportData && (
            <>
              {/* Executive Summary */}
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Executive Summary
                  </h5>
                </div>
                <div className="card-body">
                  {/* Campaign Details & Scope - Split Screen */}
                  <div className="row">
                    {/* Campaign Details */}
                    <div className="col-md-6 border-end">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Campaign Details
                      </h6>
                      <div className="row">
                        <div className="col-6">
                          <p className="mb-1"><strong>Campaign Name:</strong> {reportData.executive_summary.campaign_name}</p>
                          <p className="mb-1"><strong>Compliance Framework:</strong> {reportData.executive_summary.compliance_framework || 'N/A'}</p>
                          <p className="mb-1"><strong>Database Type:</strong> {reportData.executive_summary.database_type || 'N/A'}</p>
                        </div>
                        <div className="col-6">
                          <p className="mb-1"><strong>Start Date:</strong> {formatDateTime(reportData.executive_summary.start_date)}</p>
                          <p className="mb-1"><strong>End Date:</strong> {formatDateTime(reportData.executive_summary.end_date)}</p>
                          <p className="mb-1"><strong>Report Generated:</strong> {formatDateTime(reportData.executive_summary.report_generated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Scope Details */}
                    <div className="col-md-6">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-filter me-2"></i>
                        Scope Details
                      </h6>
                      <table className="table table-sm table-borderless mb-0" style={{ fontSize: '0.9rem' }}>
                        <tbody>
                          <tr>
                            <td style={{ width: '130px', padding: '2px 8px' }}><strong>Total Accounts:</strong></td>
                            <td style={{ padding: '2px 8px' }}><span className="text-secondary">{reportData.statistics.total_records}</span></td>
                            <td style={{ width: '130px', padding: '2px 8px' }}><strong>Accounts Included:</strong></td>
                            <td style={{ padding: '2px 8px' }}>
                              <span className={reportData.executive_summary.scope_criteria?.active_only ? 'text-warning' : 'text-info'}>
                                {reportData.executive_summary.scope_criteria?.active_only ? 'Active Only' : 'All'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2px 8px' }}><strong>In Scope:</strong></td>
                            <td style={{ padding: '2px 8px' }}><span className="text-primary">{reportData.statistics.in_scope_count}</span></td>
                            <td style={{ padding: '2px 8px' }}><strong>Out of Scope:</strong></td>
                            <td style={{ padding: '2px 8px' }}><span className="text-secondary">{reportData.statistics.out_of_scope_count}</span></td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2px 8px' }}><strong>Privilege Levels:</strong></td>
                            <td colSpan="3" style={{ padding: '2px 8px' }}>
                              {reportData.executive_summary.scope_criteria?.privilege_levels && reportData.executive_summary.scope_criteria.privilege_levels.length > 0 ? (
                                reportData.executive_summary.scope_criteria.privilege_levels.map((level, idx) => (
                                  <span key={idx} className="text-primary me-2">{level}</span>
                                ))
                              ) : (
                                <span className="text-info">All</span>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>
              </div>

              {/* Database Topology Section */}
              {reportData.topology && reportData.topology.length > 0 && (
                <TopologySection topology={reportData.topology} />
              )}

              {/* User Access Inventory */}
              <div className="card shadow-sm">
                <div className="card-header bg-dark text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-table me-2"></i>
                      Complete User Access Inventory
                    </h5>
                    <span className="text-light">
                      {getFilteredRecords().length} records
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  {/* Filters */}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by database, username, employee, attester..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filterScope}
                        onChange={(e) => setFilterScope(e.target.value)}
                      >
                        <option value="all">All Scope Status</option>
                        <option value="in_scope">In Scope Only</option>
                        <option value="out_of_scope">Out of Scope Only</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filterAttesterType}
                        onChange={(e) => setFilterAttesterType(e.target.value)}
                      >
                        <option value="all">All Attester Types</option>
                        <option value="Manager">Manager</option>
                        <option value="Account Owner">Account Owner</option>
                        <option value="Database Owner">Database Owner</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={recordsPerPage}
                        onChange={(e) => {
                          setRecordsPerPage(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value="25">25 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                        <option value="200">200 per page</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered table-striped table-hover">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>Database Name</th>
                          <th>Database Type</th>
                          <th>Application ID</th>
                          <th>Application Name</th>
                          <th>All Applications</th>
                          <th>DB Username</th>
                          <th>Account Type</th>
                          <th>Privilege Level</th>
                          <th>Employee Name</th>
                          <th>Employee Email</th>
                          <th>Job Title</th>
                          <th>Department</th>
                          <th>Employment Status</th>
                          <th>In Scope</th>
                          <th>Exclusion Reason</th>
                          <th>Attester Name</th>
                          <th>Attester Email</th>
                          <th>Attester Type</th>
                          <th>Override Applied</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedRecords().map((record, index) => (
                          <tr key={record.report_id || index}>
                            <td>{record.database_name}</td>
                            <td>{record.database_type}</td>
                            <td>{record.application_id || 'N/A'}</td>
                            <td>{record.application_name || 'N/A'}</td>
                            <td>{record.application_names || 'N/A'}</td>
                            <td><code>{record.db_username}</code></td>
                            <td>
                              <span className={
                                record.account_type === 'System Account' ? 'text-secondary' :
                                record.account_type === 'Service Account' ? 'text-warning' : 'text-info'
                              }>
                                {record.account_type}
                              </span>
                            </td>
                            <td>
                              <span className={
                                record.privilege_level === 'HIGH' ? 'text-danger' :
                                record.privilege_level === 'MEDIUM' ? 'text-warning' : 'text-success'
                              }>
                                {record.privilege_level || 'N/A'}
                              </span>
                            </td>
                            <td>{record.user_display_name || 'N/A'}</td>
                            <td>{record.user_email || 'N/A'}</td>
                            <td>{record.user_job_title || 'N/A'}</td>
                            <td>{record.user_department || 'N/A'}</td>
                            <td>{record.employment_status}</td>
                            <td>
                              <span className={record.user_in_scope?.toUpperCase() === 'YES' ? 'text-success' : 'text-secondary'}>
                                {record.user_in_scope}
                              </span>
                            </td>
                            <td>{record.exclusion_reason || '-'}</td>
                            <td>{record.manager_name || 'N/A'}</td>
                            <td>{record.manager_email || 'N/A'}</td>
                            <td>{record.attester_type || 'N/A'}</td>
                            <td>
                              <span className={record.override_applied === 'YES' ? 'text-warning' : 'text-muted'}>
                                {record.override_applied}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {getTotalPages() > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Showing {((currentPage - 1) * recordsPerPage) + 1} to{' '}
                        {Math.min(currentPage * recordsPerPage, getFilteredRecords().length)} of{' '}
                        {getFilteredRecords().length} records
                      </div>
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                            >
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          <li className="page-item active">
                            <span className="page-link">
                              Page {currentPage} of {getTotalPages()}
                            </span>
                          </li>
                          <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === getTotalPages()}
                            >
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(getTotalPages())}
                              disabled={currentPage === getTotalPages()}
                            >
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!reportData && !isLoading && !error && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Select a campaign and click "Generate Report" to view the pre-attestation baseline data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreAttestationBaselineReport;
